import {
  computed,
  nextTick,
  onBeforeUnmount,
  reactive,
  useRoute,
  useTemplateRef,
} from '#imports';
import { useBackendAPI } from '@nuxt-shared/composables/useBackendAPI';
import { useDateFormat } from '@nuxt-shared/composables/useDateFormat';
import type {
  NewsApiData,
  NewsApiItem,
  NewsApiResponse,
  NewsCardLayout,
  NewsPageState,
  NewsRequestQuery,
  NewsSearchItem,
  NewsSearchUpdate,
  NormalizedNewsData,
} from './types';

const NEWS_ENDPOINT = 'expertise/category/news/';
const PAGE_SIZE = 11;
const RELOAD_DEBOUNCE_MS = 500;

const getQueryString = (value: unknown): string => {
  if (Array.isArray(value)) return String(value[0] ?? '');
  return typeof value === 'string' ? value : '';
};

const getInitialTags = (value: string): string[] =>
  value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const getNewsDate = (item: NewsApiItem): string => {
  if (item.ACTIVE_DAY) return item.ACTIVE_DAY;
  if (!item.ACTIVE_FROM) return '';
  return useDateFormat(item.ACTIVE_FROM).default ?? '';
};

const getNewsUrl = (item: NewsApiItem): string =>
  item.LINK || (item.CODE ? `/news/${item.CODE}/` : '/news/');

const normalizeNewsData = (source: NewsApiData | null | undefined): NormalizedNewsData => {
  const items = Array.isArray(source?.ITEMS) ? source.ITEMS : [];

  return {
    title: source?.TITLE ?? '',
    description: source?.DESCRIPTION ?? '',
    pageCount: Number(source?.COUNT_PAGE) || (items.length ? 1 : 0),
    posts: items.map((item, index) => ({
      id: item.ID ?? item.CODE ?? index,
      date: getNewsDate(item),
      title: item.NAME ?? '',
      description: item.PREVIEW_TEXT_SHORT || item.PREVIEW_TEXT || '',
      pictures: item.IMAGE ?? {
        IMG_XS: item.PREVIEW_PICTURE || '',
        IMG_SM: item.PREVIEW_PICTURE || '',
      },
      tags: Array.isArray(item.TAGS) ? item.TAGS : [],
      url: getNewsUrl(item),
    })),
    tags: {
      list: Array.isArray(source?.TAGS) ? [...source.TAGS] : [],
      clickable: Boolean(source?.TAGS_CLICKABLE),
      path: source?.TAGS_PATH ?? '/news/?TAGS=',
      quantity: source?.TAGS_QUANTITY ?? {},
    },
  };
};

const createRequestQuery = (state: NewsPageState): NewsRequestQuery => ({
  p: state.page.next,
  q: state.search.value,
  queryTags: state.search.group.join(','),
  clearCache: state.initialized ? 'N' : null,
});

export async function useNewsPageModel() {
  const route = useRoute();
  const newsFeed = useTemplateRef<HTMLElement>('newsFeed');
  const routeSearch = getQueryString(route.query.q);
  const routeTags = getQueryString(route.query.TAGS);
  const routeClearCache = getQueryString(route.query.clear_cache) || null;
  const initialGroup = getInitialTags(routeTags);

  const state = reactive<NewsPageState>({
    title: '',
    description: '',
    posts: [],
    page: { next: 1, count: 0 },
    search: {
      value: routeSearch,
      group: initialGroup,
      items: [],
    },
    tags: {
      list: [],
      clickable: false,
      path: '',
      quantity: {},
    },
    initialized: false,
    loading: false,
  });

  let reloadTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const isLoadMoreVisible = computed(
    () => state.posts.length > 0 && state.page.next <= state.page.count,
  );

  const updateSearchItems = () => {
    const validGroup = state.search.group.filter((tag) => state.tags.list.includes(tag));
    state.search.group = validGroup;
    state.search.items = state.tags.list.map<NewsSearchItem>((alias, index) => ({
      id: index + 1,
      alias,
      name: alias,
      quantity: state.tags.quantity[alias],
      disabled: false,
      selected: validGroup.includes(alias),
    }));
  };

  const applyNewsData = (data: NormalizedNewsData, replace = false) => {
    if (replace || !state.initialized) {
      state.title = data.title;
      state.description = data.description;
      state.page.count = data.pageCount;
    }

    state.tags = data.tags;

    if (replace) {
      state.posts = data.posts;
    } else {
      const knownIds = new Set(state.posts.map((post) => post.id));
      state.posts.push(...data.posts.filter((post) => !knownIds.has(post.id)));
    }

    updateSearchItems();
    state.page.next += 1;
    state.initialized = true;
  };

  const updateUrl = () => {
    if (import.meta.server) return;

    const url = new URL(window.location.href);
    const selectedTags = state.search.group.join(',');

    if (selectedTags) url.searchParams.set('TAGS', selectedTags);
    else url.searchParams.delete('TAGS');

    if (state.search.value) url.searchParams.set('q', state.search.value);
    else url.searchParams.delete('q');

    window.history.pushState(null, '', url.href);
  };

  const fetchClientPage = async (): Promise<NormalizedNewsData> => {
    const response = await $fetch<NewsApiResponse>('/api/expertise/category/news/', {
      method: 'GET',
      query: createRequestQuery(state),
    });

    return normalizeNewsData(response.data);
  };

  const loadMore = async () => {
    if (state.loading || state.page.next > state.page.count) return;

    state.loading = true;
    try {
      applyNewsData(await fetchClientPage());
    } finally {
      state.loading = false;
    }
  };

  const reload = () => {
    if (reloadTimeoutId) clearTimeout(reloadTimeoutId);

    state.loading = true;
    state.posts = [];
    reloadTimeoutId = setTimeout(async () => {
      state.page.next = 1;

      try {
        applyNewsData(await fetchClientPage(), true);
        updateUrl();
        await nextTick();
        newsFeed.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } finally {
        state.loading = false;
      }
    }, RELOAD_DEBOUNCE_MS);
  };

  const updateSearch = ({ value, group }: NewsSearchUpdate) => {
    state.search.value = value;
    state.search.group = group;
  };

  // Позиция стабильна в SSR, а нужный вариант по ширине выбирают media queries.
  const getCardLayout = (index: number): NewsCardLayout => {
    const pageIndex = index % PAGE_SIZE;

    if (pageIndex === 1) return 'featured-desktop';
    if (pageIndex === 2) return 'featured-tablet';
    return 'default';
  };

  onBeforeUnmount(() => {
    if (reloadTimeoutId) clearTimeout(reloadTimeoutId);
  });

  state.loading = true;
  try {
    const response = await useBackendAPI(NEWS_ENDPOINT, {
      key: `news-feed-${encodeURIComponent(routeSearch)}-${encodeURIComponent(routeTags)}`,
      method: 'GET',
      query: {
        p: 1,
        q: routeSearch,
        queryTags: routeTags,
        clearCache: routeClearCache,
      },
    });
    applyNewsData(normalizeNewsData(response?.data.value), true);
  } finally {
    state.loading = false;
  }

  return {
    state,
    newsFeed,
    isLoadMoreVisible,
    getCardLayout,
    loadMore,
    reload,
    updateSearch,
  };
}
